'use client';

import Image from 'next/image';
import React from 'react';
import { useScroll } from 'framer-motion';
import { motion } from 'framer-motion';
import { Header } from './Header';
import { Footer } from './Footer';
import { NailServicesSection } from './NailServicesSection';
import { HoursSection } from './HoursSection';
import { SmoothScroll } from './SmoothScroll';
import { RESERVATION_URL } from '../lib/site';

export default function OngleriesContent() {
  const { scrollYProgress } = useScroll();

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-[#FEADBB] selection:text-white overflow-clip">
      <SmoothScroll />

      <Header
        brandName="Onglerie"
        items={[
          { label: 'Accueil', href: '/' },
          { label: 'Soins coiffure', href: '/#soins-coiffure' },
          { label: 'Onglerie', href: '/#onglerie' },
          { label: 'Horaires', href: '/#horaires' },
          { label: 'Avis', href: '/#avis' },
        ]}
        scrollYProgress={scrollYProgress}
      />

      {/* Hero Section */}
      <section className="relative flex items-center justify-center px-4 pb-10 pt-24 sm:px-6 sm:pb-16 sm:pt-28 md:px-12 md:pb-20 md:pt-32 lg:px-24 lg:pb-24 lg:pt-40 min-h-[auto] md:min-h-[80vh]">
        <div className="max-w-[1300px] w-full grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-8 sm:gap-10 lg:gap-20 items-center">

          {/* Left Content */}
          <div className="relative z-10 flex min-w-0 flex-col items-start">
            <div className="mb-6 flex max-w-full flex-col items-start gap-4 sm:mb-8">
              <a href={RESERVATION_URL} target="_blank" rel="noopener noreferrer" className="flex max-w-full items-center gap-2 rounded-full border border-gray-100 bg-white px-4 py-2 shadow-xl shadow-pink-500/10 transition-transform hover:scale-105">
                <span className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  ))}
                </span>
                <span className="font-bold text-sm">4.9/5 Planity</span>
              </a>

              <h1 className="max-w-[8.4ch] text-[clamp(2.45rem,14vw,5.5rem)] font-black uppercase tracking-tight leading-[0.86] sm:max-w-[8.8ch] sm:leading-[0.9] lg:max-w-none">
                <span className="text-[#FEADBB]">Onglerie</span>
                <br />
                <span className="inline-block rounded-[1.2rem] bg-[#1d1d1d] px-4 py-1 text-white sm:rounded-2xl sm:px-5 sm:py-1">
                  Poitiers
                </span>
                <br />
                <span className="block max-w-[7.5ch] sm:max-w-[8.5ch] lg:max-w-none">
                  Institut d&apos;exception
                </span>
              </h1>
            </div>

            <p className="mb-8 max-w-[17rem] text-base font-medium leading-relaxed text-[#1d1d1d]/70 sm:mb-10 sm:max-w-md sm:text-lg md:text-xl">
              L&apos;élégance des ongles en gel à Poitiers !<br />
              Une application experte pour des résultats époustouflants qui durent.
            </p>

            <a
              href={RESERVATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-[#FEADBB] px-8 py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_30px_rgba(254,173,187,0.4)] transition-all hover:bg-[#F9A8D4] sm:px-10 sm:py-4 sm:text-base"
            >
              Réserver maintenant
            </a>
          </div>

          {/* Right Image */}
          <div className="relative min-w-0">
            <div className="group relative w-full aspect-[1/1.12] overflow-hidden rounded-[2rem] bg-gray-100 shadow-2xl sm:aspect-[4/3] sm:rounded-[2.5rem] lg:aspect-[1.1/1]">
              <Image
                src="/images/optimized/onglehero-1600.jpg"
                alt="Onglerie Poitiers - Pose de manucure French rose sur mesure"
                title="Onglerie Poitiers - Expertise Eleganze"
                fill
                priority
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 92vw, 52vw"
                className="object-cover transition-transform duration-1000 group-hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-10 -left-10 hidden h-32 w-32 overflow-hidden rounded-full border-[8px] border-white shadow-2xl z-20 md:block md:-bottom-16 md:-left-16 md:h-56 md:w-56">
              <Image
                src="/images/optimized/ongle-detail-900.jpg"
                alt="Détail soin ongles Eleganze Poitiers"
                fill
                sizes="224px"
                className="object-cover"
              />
            </div>
          </div>
        </div>

        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#FEADBB]/5 rounded-full blur-[150px] -z-10 translate-x-[-30%] translate-y-[-50%]" />
      </section>

      {/* Prestations */}
      <div id="prestations" className="relative group/section z-10 bg-white">
        <NailServicesSection hideGallery={true} isFullPage={true} />
      </div>

      {/* Quote */}
      <section className="py-24 md:py-32 px-6 md:px-12 text-center max-w-4xl mx-auto flex flex-col items-center relative z-20 bg-white">
        <span className="text-5xl md:text-7xl font-serif text-[#FEADBB] mb-4 opacity-50">&ldquo;</span>
        <h2 className="text-2xl md:text-4xl font-medium leading-relaxed text-[#1d1d1d]/80">
          L&apos;onglerie n&apos;est pas seulement un soin esthétique, c&apos;est l&apos;expression même de votre personnalité.
        </h2>
        <p className="mt-8 text-gray-500 font-bold uppercase tracking-[0.25em] text-xs">
          L&apos;équipe Eleganze
        </p>
      </section>

      {/* Gallery */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 pb-24 md:pb-32 pt-10 md:pt-12 relative z-20 bg-white">
        <div className="mb-20 md:mb-24 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-[#FEADBB] font-bold mb-4">NOTRE COLLECTION</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1d1d1d] uppercase tracking-tighter">Nos créations d&apos;onglerie</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-10">
          {[
            { src: '/images/optimized/ongle1-1200.jpg', alt: 'French manucure classique amande - Eleganze', title: 'French Manucure', label: 'Classic Luxe', desc: "L'élégance intemporelle de la French manucure par nos experts.", delay: 0, extra: '' },
            { src: '/images/optimized/ongle2-1200.jpg', alt: 'Onglerie Poitiers - Effet Chrome Glazed perle', title: 'Effet Chrome Ongles Poitiers', label: 'Chrome Pearl', desc: 'Reflets nacrés et finitions miroir haute précision.', delay: 0.1, extra: 'mt-0 md:mt-12' },
            { src: '/images/optimized/ongle5-1200.jpg', alt: 'Onglerie Poitiers - Nail Art créatif avec motifs coeurs', title: 'Nail Art Créatif Poitiers', label: 'Creative Hearts', desc: 'Nail art sur mesure pour exprimer votre personnalité.', delay: 0.2, extra: '' },
            { src: '/images/optimized/ongle3-1200.jpg', alt: 'Onglerie Poitiers - Pose gel naturel', title: 'Pose Gel Poitiers', label: 'Pure Natural', desc: 'La pureté d\'une pose naturelle parfaitement exécutée.', delay: 0, extra: 'sm:col-span-2 xl:col-span-1' },
            { src: '/images/optimized/ongle6-1200.jpg', alt: 'Onglerie Poitiers - Design moderne', title: 'Design Ongles Poitiers', label: 'Modern Art', desc: 'Des designs audacieux pour un style affirmé.', delay: 0.1, extra: 'mt-0 md:mt-12' },
            { src: '/images/optimized/ongle4-1200.jpg', alt: 'Onglerie Poitiers - Pose sophistiquée', title: 'Expertise Ongles Poitiers', label: 'Sophisticated', desc: "L'exigence du détail pour une finition haute couture.", delay: 0.2, extra: '' },
          ].map((card) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: card.delay }}
              className={`rounded-[3rem] aspect-[4/5] relative overflow-hidden group shadow-2xl ${card.extra}`}
            >
              <Image src={card.src} alt={card.alt} title={card.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw" className="object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out" />
              <div className="absolute inset-x-6 bottom-8 backdrop-blur-xl bg-white/20 border border-white/30 p-6 rounded-[2rem] shadow-2xl transition-all duration-300 group-hover:bg-[#FEADBB]/20">
                <h3 className="text-white font-black text-xl mb-1 uppercase tracking-tight">{card.label}</h3>
                <p className="text-white/80 text-sm font-medium">{card.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-32 relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-black text-[#1d1d1d] uppercase tracking-tighter mb-8">Un service expert pour votre style unique</h2>
            <p className="text-lg text-gray-600 leading-relaxed font-medium">
              Parce que chaque détail compte, nous prenons le temps d&apos;analyser vos besoins pour vous offrir un soin des mains et une pose sur mesure. Notre objectif est de sublimer votre apparence dans une ambiance détendue et professionnelle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 text-center">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#1d1d1d] flex items-center justify-center text-white mb-8 shadow-xl">
                <svg className="h-9 w-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3.5l1.8 4.9 4.7 1.6-4.7 1.6-1.8 4.9-1.8-4.9-4.7-1.6 4.7-1.6L12 3.5z" /><path d="M19 12.5l.9 2.5 2.4.8-2.4.8-.9 2.5-.9-2.5-2.4-.8 2.4-.8.9-2.5z" /></svg>
              </div>
              <h3 className="text-2xl font-black text-[#1d1d1d] mb-4 uppercase tracking-tight">Qualité Salon</h3>
              <p className="text-gray-600 text-base leading-relaxed">Nous utilisons des produits de haute qualité adaptés à vos ongles et à votre peau.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#FEADBB] flex items-center justify-center text-white mb-8 shadow-xl">
                <svg className="h-9 w-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8.5" r="4.5" /><path d="M9.5 12.5 8 21l4-2.2L16 21l-1.5-8.5" /></svg>
              </div>
              <h3 className="text-2xl font-black text-[#1d1d1d] mb-4 uppercase tracking-tight">Art & Passion</h3>
              <p className="text-gray-600 text-base leading-relaxed">Chaque pose est une création unique réalisée avec passion.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#1d1d1d] flex items-center justify-center text-white mb-8 shadow-xl">
                <svg className="h-9 w-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 5c-6 0-10 2.5-12 7s-1 7 1 7 4.5-1 7-3 4-5.5 4-11Z" /><path d="M7 17c2-3 5-5 10-7" /></svg>
              </div>
              <h3 className="text-2xl font-black text-[#1d1d1d] mb-4 uppercase tracking-tight">Respect du soin</h3>
              <p className="text-gray-600 text-base leading-relaxed">Utilisation de produits sûrs et respectueux de vos ongles.</p>
            </div>
          </div>
        </div>
      </section>

      <HoursSection
        isPink={true}
        description="Parce que chaque détail compte, nous prenons le temps d'analyser vos besoins pour vous offrir un soin des mains et une pose sur mesure. Notre objectif est de sublimer votre apparence dans une ambiance détendue et professionnelle."
      />

      <Footer brandName="Onglerie" />
    </div>
  );
}
